import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UseSelectCoreProps } from '../types';
import { Option } from '@/legacy/types';

type VisibleValsMemo = {
	(): (string | number)[];
};

export function useSelectCore({
	value,
	onChange,
	onSelect,
	options,
	multiple = false,
	filter,
	selectAllRow = false,
	creatable = false,
	onCreateOption,
	dontOpenDropdownIfNoOptions,
	reorderSelectedToTop = true,
}: UseSelectCoreProps) {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const interactionModeRef = useRef<'keyboard' | 'pointer'>('pointer');

	const normalize = (s: string) =>
		s
			.normalize?.('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase() ?? s.toLowerCase();

	const asArray = (v: typeof value): (string | number)[] =>
		Array.isArray(v) ? (v as (string | number)[]) : v == null ? [] : [v as any];

	const isKey = (v: unknown): v is string | number =>
		typeof v === 'string' || typeof v === 'number';

	const selectedArr = useMemo<(string | number)[]>(() => asArray(value), [value]);

	const isSelected = useCallback(
		(val: string | number) => selectedArr.includes(val),
		[selectedArr],
	);

	const toggle = useCallback(
		(val: string | number) => {
			onSelect?.(val);

			if (!multiple) {
				onChange(val);
				if (creatable) setInputValue(options.find((o) => o.value === val)?.label || '');
				setOpen(false);
				return;
			}

			const next = selectedArr.includes(val)
				? selectedArr.filter((v) => v !== val)
				: [...selectedArr, val];
			onChange(next);
		},
		[multiple, onChange, onSelect, selectedArr],
	);

	const visibleOptions = useMemo(() => {
		const q = normalize(inputValue.trim());
		const base = options ?? [];
		if (!q) return base;
		if (filter) return base.filter((o) => filter(o, q));
		return base?.filter(
			(o) => normalize(o.label ?? '').includes(q) || normalize(String(o.value ?? '')).includes(q),
		);
	}, [options, inputValue, filter]);

	const labelCacheRef = useRef(new Map<string | number, string>());

	const optionMap = useMemo(() => {
		const m = new Map<string | number, Option>();
		for (const o of options ?? []) {
			if (isKey(o.value)) m.set(o.value, o);
		}
		return m;
	}, [options]);

	const getLabel = useCallback(
		(v: string | number) => {
			return optionMap.get(v)?.label ?? labelCacheRef.current.get(v) ?? String(v);
		},
		[optionMap],
	);

	const selectedOptions = useMemo(() => {
		return asArray(value).map((v) => optionMap.get(v) ?? { value: v, label: getLabel(v) });
	}, [value, getLabel, optionMap]);

	// Sync inputValue with the selected option's label for single creatable selects
	const inputValueRef = useRef(inputValue);
	inputValueRef.current = inputValue;

	const hasInitializedRef = useRef(false);

	useEffect(() => {
		if (multiple || !creatable || open) return;
		if (value == null) return;

		if (hasInitializedRef.current && inputValueRef.current.trim() === '') {
			onChange(null);
			return;
		}

		const label = optionMap.get(value as string | number)?.label;
		if (label) {
			setInputValue(label);
			hasInitializedRef.current = true;
			return;
		}
	}, [value, optionMap, multiple, creatable, open, onChange]);

	const trimmed = inputValue.trim();
	const hasSelectAllRow =
		!!selectAllRow && (trimmed === '' || (trimmed !== '' && visibleOptions.length > 0));

	const selectedOnOpenRef = useRef<Set<string | number>>(new Set());
	const [orderedOnOpen, setOrderedOnOpen] = useState<Option[] | null>(null);

	useEffect(() => {
		if (open) selectedOnOpenRef.current = new Set(selectedArr);
	}, [open]);

	useEffect(() => {
		if (!open) {
			setOrderedOnOpen(null);
			return;
		}

		if (!reorderSelectedToTop) {
			setOrderedOnOpen(visibleOptions);
			return;
		}

		const snap = selectedOnOpenRef.current;
		if (snap.size === 0) {
			setOrderedOnOpen(visibleOptions);
			return;
		}

		const selected: Option[] = [];
		const unselected: Option[] = [];

		for (const o of visibleOptions) {
			snap.has(o.value) ? selected.push(o) : unselected.push(o);
		}

		setOrderedOnOpen([...selected, ...unselected]);
	}, [open, visibleOptions, reorderSelectedToTop]);

	const displayOptions = open && orderedOnOpen ? orderedOnOpen : visibleOptions;

	const visibleVals: (string | number)[] = useMemo<(string | number)[]>(
		(() => visibleOptions.map((o: Option) => o.value).filter(isKey)) as VisibleValsMemo,
		[visibleOptions],
	);

	const allVisibleSelected =
		visibleVals.length > 0 && visibleVals.every((v) => selectedArr.includes(v));

	const toggleAllVisible = useCallback(() => {
		if (!multiple) return;
		if (allVisibleSelected) {
			onChange(selectedArr.filter((v) => !visibleVals.includes(v)));
		} else {
			onChange(Array.from(new Set([...selectedArr, ...visibleVals])));
		}
	}, [multiple, allVisibleSelected, onChange, selectedArr, visibleVals]);

	const handleCreateOption = async (label: string) => {
		if (!onCreateOption) return null;
		const newOption = await onCreateOption(label);
		if (newOption) {
			labelCacheRef.current.set(newOption.value, newOption.label);
			onChange(multiple ? [...selectedArr, newOption.value] : newOption.value);
			setInputValue(multiple ? '' : newOption.label);
			setOpen(false);
			queueMicrotask(() => {
				setOpen(true);
				setHighlightedIndex(0);
			});
		}
		return newOption;
	};

	const baseIndex = hasSelectAllRow ? 1 : 0;
	const creatableIndex = creatable ? baseIndex + displayOptions.length : -1;
	const itemCount = baseIndex + displayOptions.length + (creatable ? 1 : 0);

	const onEnter = (e: React.KeyboardEvent) => {
		if (e.key !== 'Enter') return;
		e.preventDefault();

		const raw = inputValue.trim();
		const exists: boolean =
			!!raw && options.some((o: Option) => normalize(o.label) === normalize(raw));

		if (hasSelectAllRow && highlightedIndex === 0) {
			toggleAllVisible();
			return;
		}

		if (highlightedIndex >= baseIndex && highlightedIndex < baseIndex + displayOptions.length) {
			const opt = displayOptions[highlightedIndex - baseIndex];
			if (opt) {
				toggle(opt.value);
				if (multiple) setInputValue('');
				else setOpen(false);
			}
			return;
		}
		if (creatable && raw && !exists) {
			void handleCreateOption?.(raw);
		}
	};

	const onInputChange = useCallback(
		(v: string) => {
			setInputValue(v);

			const hasQuery = v.trim() !== '';

			const willHaveResults =
				!dontOpenDropdownIfNoOptions ||
				(Array.isArray(options) &&
					(filter
						? options.some((o) => filter(o, normalize(v?.trim() ?? '')))
						: options.some((o) =>
								String(o.label ?? '')
									?.toLowerCase()
									.includes(v.trim()?.toLowerCase()),
							)));

			if (hasQuery && willHaveResults) {
				if (!open) setOpen(true);
				setHighlightedIndex(selectAllRow ? 1 : 0);
			}
		},
		[open, options, filter, dontOpenDropdownIfNoOptions, selectAllRow],
	);

	// ---- NEW: clamp highlighted index any time the rows set changes
	useEffect(() => {
		// first selectable index
		const first = hasSelectAllRow ? 1 : 0;
		if (itemCount === 0) {
			setHighlightedIndex(-1);
			return;
		}
		setHighlightedIndex((idx) => {
			if (idx < first) return first;
			const last = itemCount - 1;
			if (idx > last) return last;
			return idx;
		});
	}, [hasSelectAllRow, itemCount]);

	const unselectAll = useCallback(() => {
		if (!multiple) return;
		onChange([]);
	}, [multiple, onChange]);

	return {
		open,
		setOpen,
		inputValue,
		setInputValue,
		onInputChange,
		normalizeInputValue: normalize,
		highlightedIndex,
		setHighlightedIndex,
		handleCreateOption,

		options,
		visibleOptions,
		displayOptions,
		value,

		isSelected,
		toggle,

		allVisibleSelected,
		toggleAllVisible,

		multiple,
		creatable,
		selectAllRow,
		hasSelectAllRow,

		itemCount,
		creatableIndex,
		baseIndex,

		onEnter,

		selectedOptions,
		getLabel,
		unselectAll,
		interactionModeRef,
	};
}
